import { useState, useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useReactToPrint } from 'react-to-print';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Skeleton } from '../components/ui/skeleton';
import { WritableSelect } from '../components/ui/writable-select';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import CarInvoice, { saleToInvoiceCar } from '../components/CarInvoice';
import type { Seller, WarehouseCar, Sale, CarBrand, CarModel } from '../types/car';
import { saleFormSchema, type SaleFormData } from '../lib/validations';
import { api } from '../lib/api';
import { toast } from 'sonner';

export default function SellCar() {
  const { t } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [warehouseCars, setWarehouseCars] = useState<WarehouseCar[]>([]);
  const [sellers, setSellers] = useState<Seller[]>([]);
  const [brands, setBrands] = useState<CarBrand[]>([]);
  const [models, setModels] = useState<CarModel[]>([]);
  const [selectedWarehouseCar, setSelectedWarehouseCar] = useState<WarehouseCar | null>(null);
  const [selectedSeller, setSelectedSeller] = useState('');
  const [selectedBrand, setSelectedBrand] = useState('');
  const [selectedModel, setSelectedModel] = useState('');
  const [soldSale, setSoldSale] = useState<Sale | null>(null);
  const [showInvoice, setShowInvoice] = useState(false);
  const [clearDialogOpen, setClearDialogOpen] = useState(false);
  const invoiceRef = useRef<HTMLDivElement>(null);

  const {
    register,
    handleSubmit,
    setValue,
    reset,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<SaleFormData>({
    resolver: zodResolver(saleFormSchema) as any,
    defaultValues: {
      sellMode: 'direct' as const,
      warehouseCarId: 0,
      brandId: 0,
      modelId: 0,
      carYear: new Date().getFullYear(),
      carColor: '',
      carPlateNo: '',
      carChassisNo: '',
      carRegisteredOwner: '',
      salePrice: 0,
      deposit: 0,
      currency: 'USD',
      sellerName: '',
      sellerIdNo: '',
      sellerAddress: '',
      sellerContact: '',
      buyerName: '',
      buyerIdNo: '',
      buyerAddress: '',
      buyerContact: '',
      notes: '',
    },
  });

  const handlePrint = useReactToPrint({
    contentRef: invoiceRef,
    documentTitle: soldSale ? `Sale Invoice - ${soldSale.warehouseCar.brand.name} ${soldSale.warehouseCar.model.name}` : 'Sale Invoice',
    pageStyle: `@page { size: A4; margin: 0; } @media print { body { -webkit-print-color-adjust: exact; } }`,
  });

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [whRes, sellersRes, brandsRes, modelsRes] = await Promise.all([
        api.get('/api/warehouse-cars?status=IN_STOCK&limit=200'),
        api.get('/api/sellers'),
        api.get('/api/brands'),
        api.get('/api/models'),
      ]);
      const wh = (whRes as { warehouseCars?: WarehouseCar[] })?.warehouseCars ?? whRes;
      setWarehouseCars(Array.isArray(wh) ? wh : []);
      const s = (sellersRes as { sellers?: Seller[] })?.sellers ?? sellersRes;
      setSellers(Array.isArray(s) ? s : []);
      setBrands(Array.isArray(brandsRes) ? brandsRes : []);
      setModels(Array.isArray(modelsRes) ? modelsRes : []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const sellMode = watch('sellMode');
  const warehouseCarId = watch('warehouseCarId');
  const currentBrandId = selectedBrand ? brands.find((b) => b.name === selectedBrand)?.id ?? 0 : 0;

  useEffect(() => {
    if (sellMode !== 'warehouse') return;
    const id = typeof warehouseCarId === 'string' ? parseInt(warehouseCarId, 10) : warehouseCarId;
    const car = warehouseCars.find((c) => c.id === id) ?? null;
    setSelectedWarehouseCar(car);
    if (car?.seller) {
      setValue('sellerName', car.seller.name);
      setValue('sellerIdNo', car.seller.idNo);
      setValue('sellerAddress', car.seller.address || '');
      setValue('sellerContact', car.seller.contact);
      setSelectedSeller(car.seller.name);
    } else {
      setSelectedSeller('');
      setValue('sellerName', '');
      setValue('sellerIdNo', '');
      setValue('sellerAddress', '');
      setValue('sellerContact', '');
    }
    if (car) {
      setValue('currency', (car as any).purchaseCurrency ?? 'USD');
    }
  }, [sellMode, warehouseCarId, warehouseCars, setValue]);

  const createBrand = async (name: string) => {
    const newBrand = await api.post('/api/brands', { name, active: true });
    setBrands((prev) => [...prev, newBrand]);
    setSelectedBrand(newBrand.name);
    setValue('brandId', newBrand.id);
    setSelectedModel('');
    setValue('modelId', 0);
  };

  const createModel = async (name: string) => {
    const newModel = await api.post('/api/models', { name, brandId: currentBrandId, active: true });
    setModels((prev) => [...prev, newModel]);
    setSelectedModel(newModel.name);
    setValue('modelId', newModel.id);
  };

  const handleBrandChange = (name: string) => {
    setSelectedBrand(name);
    const b = brands.find((x) => x.name === name);
    setValue('brandId', b ? b.id : 0);
    setSelectedModel('');
    setValue('modelId', 0);
  };

  const handleModelChange = (name: string) => {
    setSelectedModel(name);
    const m = models.find((x) => x.name === name);
    setValue('modelId', m ? m.id : 0);
  };

  const createSeller = async (name: string) => {
    const contact = watch('sellerContact') || '';
    const newSeller = await api.post('/api/sellers', {
      name,
      idNo: watch('sellerIdNo') || undefined,
      address: watch('sellerAddress') || undefined,
      contact: contact || 'N/A',
    });
    setSellers((prev) => [...prev, newSeller]);
    setSelectedSeller(newSeller.name);
    setValue('sellerName', newSeller.name);
    setValue('sellerContact', newSeller.contact);
    setValue('sellerIdNo', newSeller.idNo || '');
    setValue('sellerAddress', newSeller.address || '');
  };

  const handleSellerChange = (name: string) => {
    setSelectedSeller(name);
    const s = sellers.find((x) => x.name === name);
    if (s) {
      setValue('sellerName', s.name);
      setValue('sellerContact', s.contact);
      setValue('sellerIdNo', s.idNo || '');
      setValue('sellerAddress', s.address || '');
    } else {
      setValue('sellerName', '');
      setValue('sellerContact', '');
      setValue('sellerIdNo', '');
      setValue('sellerAddress', '');
    }
  };


  const clearForm = () => {
    reset({
      sellMode: 'direct',
      warehouseCarId: 0,
      brandId: 0,
      modelId: 0,
      carYear: new Date().getFullYear(),
      carColor: '',
      carPlateNo: '',
      carChassisNo: '',
      carRegisteredOwner: '',
      salePrice: 0,
      deposit: 0,
      currency: 'USD',
      sellerName: '',
      sellerIdNo: '',
      sellerAddress: '',
      sellerContact: '',
      buyerName: '',
      buyerIdNo: '',
      buyerAddress: '',
      buyerContact: '',
      notes: '',
    });
    setSelectedWarehouseCar(null);
    setSelectedSeller('');
    setSelectedBrand('');
    setSelectedModel('');
  };

  const onSubmit = async (data: SaleFormData) => {
    try {
      let warehouseCarIdToUse: number;

      if (data.sellMode === 'direct') {
        // Ensure we have a seller (create if user entered name/contact but didn't select)
        let sellerId: number | undefined;
        if (selectedSeller) {
          const s = sellers.find((x) => x.name === selectedSeller);
          sellerId = s?.id;
        } else if ((data.sellerName ?? '').trim()) {
          const newSeller = await api.post('/api/sellers', {
            name: (data.sellerName ?? '').trim(),
            idNo: data.sellerIdNo || undefined,
            address: data.sellerAddress || undefined,
            contact: (data.sellerContact ?? '').trim() || 'N/A',
          });
          sellerId = newSeller.id;
          setSellers((prev) => [...prev, newSeller]);
        }

        const saleCurrency = data.currency ?? 'USD';
        const newCar = await api.post('/api/warehouse-cars', {
          brandId: data.brandId ?? 0,
          modelId: data.modelId ?? 0,
          year: data.carYear ?? new Date().getFullYear(),
          color: (data.carColor ?? '').trim() || 'N/A',
          chassisNo: (data.carChassisNo ?? '').trim() || null,
          plateNumber: (data.carPlateNo ?? '').trim() || null,
          registeredOwner: (data.carRegisteredOwner ?? '').trim() || null,
          purchasePrice: 0,
          purchaseCurrency: saleCurrency,
          sellerId: sellerId ?? undefined,
        });
        warehouseCarIdToUse = newCar.id;
      } else {
        const whId = typeof data.warehouseCarId === 'string' ? parseInt(data.warehouseCarId, 10) : data.warehouseCarId;
        if (!whId) {
          toast.error('Please select a car from warehouse');
          return;
        }
        warehouseCarIdToUse = whId;
      }

      const saleCurrency = (selectedWarehouseCar as any)?.purchaseCurrency ?? data.currency ?? 'USD';
      const sale = await api.post('/api/sales', {
        warehouseCarId: warehouseCarIdToUse,
        buyerId: data.buyerId || undefined,
        salePrice: typeof data.salePrice === 'string' ? parseFloat(data.salePrice) : data.salePrice,
        deposit: typeof data.deposit === 'string' ? parseFloat(data.deposit) : (data.deposit ?? 0),
        currency: saleCurrency,
        sellerName: data.sellerName || undefined,
        sellerIdNo: data.sellerIdNo || undefined,
        sellerAddress: data.sellerAddress || undefined,
        sellerContact: data.sellerContact || undefined,
        buyerName: data.buyerName || undefined,
        buyerIdNo: data.buyerIdNo || undefined,
        buyerAddress: data.buyerAddress || undefined,
        buyerContact: data.buyerContact || undefined,
        notes: data.notes || undefined,
      });
      const depositNum = typeof data.deposit === 'string' ? parseFloat(data.deposit) : (data.deposit ?? 0);
      if (depositNum > 0) {
        try {
          await api.post('/api/payments', {
            saleId: sale.id,
            amount: depositNum,
            currency: sale.currency || 'USD',
            notes: 'Initial deposit',
          });
        } catch {
          // ignore
        }
      }
      const fullSale = await api.get(`/api/sales/${sale.id}`);
      setSoldSale(fullSale);
      setShowInvoice(true);
      clearForm();
      setTimeout(() => handlePrint(), 100);
    } catch (e: any) {
      console.error(e);
      toast.error(e?.message || (e?.error ?? 'Failed to record sale'));
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Skeleton className="h-12 w-48 rounded-lg" />
          <span className="text-sm text-slate-500">Loading…</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200">
        <div className="w-full px-4 sm:px-6 lg:px-8 py-6 flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold text-slate-900 tracking-tight">{t('sellCar.title')}</h1>
            <p className="text-slate-500 text-sm mt-0.5">{t('sellCar.subtitle')}</p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2">
              <Label className="text-sm font-medium text-slate-700 whitespace-nowrap">{t('sellCar.source')}</Label>
              <Select
                value={watch('sellMode') ?? 'direct'}
                onValueChange={(v: 'warehouse' | 'direct') => {
                  setValue('sellMode', v);
                  if (v === 'direct') {
                    setValue('warehouseCarId', 0);
                    setSelectedWarehouseCar(null);
                  } else {
                    setValue('brandId', 0);
                    setValue('modelId', 0);
                    setValue('carYear', new Date().getFullYear());
                    setValue('carColor', '');
                    setValue('carPlateNo', '');
                    setValue('carChassisNo', '');
                    setValue('carRegisteredOwner', '');
                    setSelectedBrand('');
                    setSelectedModel('');
                  }
                }}
              >
                <SelectTrigger className="w-[200px] rounded-lg border-slate-200">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="warehouse">{t('sellCar.sellModeWarehouse')}</SelectItem>
                  <SelectItem value="direct">{t('sellCar.sellModeDirect')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Dialog open={clearDialogOpen} onOpenChange={setClearDialogOpen}>
              <DialogTrigger asChild>
                <Button variant="destructive" size="sm">{t('sellCar.clear')}</Button>
              </DialogTrigger>
              <DialogContent className="rounded-2xl border border-slate-200">
                <DialogHeader>
                  <DialogTitle>{t('sellCar.clearForm')}</DialogTitle>
                  <DialogDescription>{t('sellCar.clearFormConfirm')}</DialogDescription>
                </DialogHeader>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setClearDialogOpen(false)} className="border-slate-200 text-slate-700 hover:bg-slate-50">
                    {t('sellCar.cancel')}
                  </Button>
                  <Button variant="destructive" onClick={() => { clearForm(); setClearDialogOpen(false); }}>
                    {t('sellCar.clearForm')}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </header>

      <main className="w-full px-4 sm:px-6 lg:px-8 py-8">
        <Card className="rounded-2xl border border-slate-200 shadow-sm">
          <CardContent className="p-6 sm:p-8">
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-slate-900 border-b border-slate-200 pb-2">{t('sellCar.carDetails')}</h3>
                {watch('sellMode') === 'warehouse' ? (
                  <>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="text-slate-700">{t('sellCar.selectCarFromWarehouse')} *</Label>
                        <Select
                          value={watch('warehouseCarId') ? String(watch('warehouseCarId')) : ''}
                          onValueChange={(v) => setValue('warehouseCarId', v ? parseInt(v, 10) : 0)}
                        >
                          <SelectTrigger className="w-full rounded-lg border-slate-200">
                            <SelectValue placeholder="-- Select car --" />
                          </SelectTrigger>
                          <SelectContent>
                            {warehouseCars.map((c) => (
                              <SelectItem key={c.id} value={String(c.id)}>
                                #{c.id} – {c.brand.name} {c.model.name} {c.year} {c.color} {c.plateNumber ? `(${c.plateNumber})` : ''}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {warehouseCars.length === 0 && (
                          <p className="text-sm text-amber-800">No cars in warehouse. Add cars in Warehouse first.</p>
                        )}
                      </div>
                    </div>
                    {selectedWarehouseCar && (
                      <p className="text-sm text-slate-600">
                        Selected: {selectedWarehouseCar.brand.name} {selectedWarehouseCar.model.name} • {selectedWarehouseCar.year} • {selectedWarehouseCar.color}
                      </p>
                    )}
                  </>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div className="space-y-2">
                      <Label>{t('sellCar.brand')} *</Label>
                      <WritableSelect
                        value={selectedBrand}
                        onValueChange={handleBrandChange}
                        options={brands}
                        onCreateNew={createBrand}
                        placeholder={t('sellCar.brand')}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>{t('sellCar.model')} *</Label>
                      <WritableSelect
                        value={selectedModel}
                        onValueChange={handleModelChange}
                        options={models.filter((m) => m.brandId === currentBrandId)}
                        onCreateNew={createModel}
                        placeholder={t('sellCar.model')}
                        disabled={!currentBrandId}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>{t('sellCar.year')} *</Label>
                      <Input type="number" {...register('carYear', { valueAsNumber: true })} />
                    </div>
                    <div className="space-y-2">
                      <Label>{t('sellCar.color')}</Label>
                      <Input {...register('carColor')} />
                    </div>
                    <div className="space-y-2">
                      <Label>{t('sellCar.plateNumber')}</Label>
                      <Input {...register('carPlateNo')} />
                    </div>
                    <div className="space-y-2">
                      <Label>{t('sellCar.chassisNo')}</Label>
                      <Input {...register('carChassisNo')} />
                    </div>
                    <div className="space-y-2">
                      <Label>{t('sellCar.registeredOwner')}</Label>
                      <Input {...register('carRegisteredOwner')} placeholder="صاحب سنوية" />
                    </div>
                  </div>
                )}
              </div>

              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-slate-900 border-b border-slate-200 pb-2">{t('sellCar.sellerDetails')}</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
                  <div className="space-y-2">
                    <Label>{t('sellCar.selectSeller')}</Label>
                    <WritableSelect
                      value={selectedSeller}
                      onValueChange={handleSellerChange}
                      options={sellers}
                      onCreateNew={createSeller}
                      placeholder={t('sellCar.selectSellerPlaceholder')}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>{t('sellCar.sellerName')}</Label>
                    <Input {...register('sellerName')} placeholder="Who we bought from" />
                  </div>
                  <div className="space-y-2">
                    <Label>{t('sellCar.sellerContact')}</Label>
                    <Input {...register('sellerContact')} />
                  </div>
                  <div className="space-y-2">
                    <Label>{t('sellCar.sellerIdNo')}</Label>
                    <Input {...register('sellerIdNo')} />
                  </div>
                  <div className="space-y-2">
                    <Label>{t('sellCar.sellerAddress')}</Label>
                    <Input {...register('sellerAddress')} />
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-slate-900 border-b border-slate-200 pb-2">{t('sellCar.buyerDetails')}</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div className="space-y-2">
                    <Label>{t('sellCar.buyerName')}</Label>
                    <Input {...register('buyerName')} placeholder="Buyer name" />
                  </div>
                  <div className="space-y-2">
                    <Label>{t('sellCar.buyerContact')} *</Label>
                    <Input {...register('buyerContact')} placeholder="Contact" />
                  </div>
                  <div className="space-y-2">
                    <Label>{t('sellCar.buyerIdNo')}</Label>
                    <Input {...register('buyerIdNo')} placeholder="ID No" />
                  </div>
                  <div className="space-y-2">
                    <Label>{t('sellCar.buyerAddress')}</Label>
                    <Input {...register('buyerAddress')} placeholder="Address" />
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-slate-900 border-b border-slate-200 pb-2">{t('sellCar.paymentDetails')}</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>{t('sellCar.salePrice')} *</Label>
                    <Input type="number" step="any" {...register('salePrice')} />
                    {errors.salePrice && <p className="text-red-600 text-sm">{errors.salePrice.message}</p>}
                  </div>
                  <div className="space-y-2">
                    <Label>{t('sellCar.deposit')}</Label>
                    <Input type="number" step="any" {...register('deposit')} />
                  </div>
                  <div className="space-y-2">
                    <Label>Currency</Label>
                    {watch('sellMode') === 'direct' ? (
                      <Select
                        value={watch('currency') || 'USD'}
                        onValueChange={(v) => setValue('currency', v as 'USD' | 'IQD')}
                      >
                        <SelectTrigger className="w-full rounded-lg border-slate-200">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="USD">USD</SelectItem>
                          <SelectItem value="IQD">IQD</SelectItem>
                        </SelectContent>
                      </Select>
                    ) : (
                      <>
                        <Input
                          value={watch('currency') || (selectedWarehouseCar && ((selectedWarehouseCar as any).purchaseCurrency ?? 'USD')) || 'USD'}
                          readOnly
                          className="bg-slate-50 cursor-not-allowed border-slate-200"
                        />
                        <input type="hidden" {...register('currency')} />
                        <p className="text-xs text-slate-500">Matches purchase currency (cannot be changed)</p>
                      </>
                    )}
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label>{t('sellCar.notes')}</Label>
                <Input {...register('notes')} placeholder={t('sellCar.notesPlaceholder')} />
              </div>

              <div className="flex gap-4">
                <Button type="submit" disabled={isSubmitting} className="bg-slate-900 hover:bg-slate-800 text-white rounded-lg px-4 py-2.5 text-sm font-medium disabled:opacity-50 disabled:pointer-events-none">
                  {isSubmitting ? t('sellCar.selling') : t('sellCar.sellCar')}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </main>

      {showInvoice && soldSale && (
        <CarInvoice ref={invoiceRef} car={saleToInvoiceCar(soldSale)} isReprint={false} />
      )}
    </div>
  );
}
